<template>
  <div
    :class="{
      'height-full': true,
      'mobile-table': $device.isMobile,
      'desktop-table': !$device.isMobile,
    }"
    style="overflow-y: auto"
  >
    <div class="d-flex pa-2">
      <v-spacer />
      <slot name="right-actions" />
    </div>
    <v-divider />
    <v-data-table-server
      v-resize="onResize"
      :items="data.items"
      :headers="headers"
      :loading="!!loading"
      :items-length="data.count"
      :items-per-page-options="itemsPerPageOptions"
      :height="windowSize.y - heightOffset"
      fixed-header
      @update:options="updateOptions"
    >
      <template v-for="header in headers" #[`item.${header.key}`]="{ item }">
        <slot :name="`item.${header.key}`" :item="item">
          {{ item[header.key] }}
        </slot>
      </template>
    </v-data-table-server>
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, type Ref, computed, onMounted } from "vue";
import { APIObject, DatabaseObject, PaginatedResponse } from "@/classes";
import { useFilterStore } from "@/store/filter";
import { FilterDefinition } from "@/types/filter";
import { DataTableHeader, DataTableOptions } from "@/types/table";
import { setupApiQueue } from "../api-queue";
import { setupWindowSize } from "./window";

const props = withDefaults(
  defineProps<{
    table: string;
    tableClass: typeof DatabaseObject;
    filterDefinitions: FilterDefinition[];
    headers: DataTableHeader[];
    itemsPerPageOptions?: (number | { title: string; value: number })[];
    heightOffset?: number;
  }>(),
  {
    heightOffset: 180,
    itemsPerPageOptions: () => [
      { value: 10, title: "10" },
      { value: 25, title: "25" },
      { value: 50, title: "50" },
      { value: 100, title: "100" },
    ],
  },
);

const filterStore = useFilterStore();
filterStore.initialise(props.table, props.filterDefinitions);

watch(
  () => filterStore.values,
  async () => {
    await getData();
  },
  { deep: true },
);

const { loading, queueApiAction } = setupApiQueue();

const options = ref<DataTableOptions>({
  itemsPerPage: 10,
  page: 1,
  sortBy: [],
});
const data = ref<PaginatedResponse<DatabaseObject>>(
  new PaginatedResponse<DatabaseObject>(),
);

const updateOptions = async (dataTableOptions: DataTableOptions) => {
  options.value = dataTableOptions;
  await getData();
};

const getData = async () => {
  const sorts = options.value.sortBy.map((sort) => {
    const header = props.headers.find((h) => h.key === sort.key) as
      | DataTableHeader
      | undefined;
    return [header?.sortkey ?? sort.key, sort.order === "desc"] as [
      string,
      boolean,
    ];
  });
  await queueApiAction(async () => {
    data.value = await props.tableClass.paginated(
      filterStore.values,
      sorts,
      options.value.page - 1,
      options.value.itemsPerPage,
    );
  });
};

const { windowSize, onResize } = setupWindowSize();

defineExpose({ getData });
</script>

<style lang="scss" scoped>
.actions {
  height: 64px;
}

.double-entry {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  padding-top: 4px;
  padding-bottom: 4px;
}

.single-entry {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  padding-top: 4px;
  padding-bottom: 4px;
}

.secondary-field {
  font-size: 0.8rem;
  color: #999;
}

// Remove bottom border of last row
.paginated-table ::v-deep tbody tr:nth-last-child(2) td {
  border-bottom: none !important;
}

// Make aggregate row sticky
.sticky-aggregate th {
  position: sticky;
  bottom: 0;
  background-color: white;
  border-top: thin solid rgba(0, 0, 0, 0.12);
}

.mobile-table,
.desktop-table {
  background-color: white;
}
</style>

<style lang="scss">
.v-data-table-header th {
  white-space: nowrap;
}

.mobile-table {
  th {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
  td {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
}
</style>
